import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { MLModalService } from 'ml-portals-services-react';
import { MLRewriteService } from 'ml-capability-rewrite';
import {
    Coverage,
    MLHOEditCoveragesComponent,
    MLPUPEditCoveragesComponent,
    MLPAAgentEditCoveragesComponent
} from 'ml-components-common-react';
import { MLUWIssuesPanelComponent } from 'gw-capability-gateway-quoteandbind-common-react';
import { MLUWService } from 'gw-capability-gateway';
import { Identify  } from "gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent";
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import Rewrite from '../../model/Rewrite';

import MLRewriteStickyHeader from '../../components/MLRewriteStickyHeader/MLRewriteStickyHeader';
import useCheckRewriteSteps from '../../util/useCheckRewriteSteps';
import metadata from './MLRewriteCoveragesPage.metadata.json5';

import messages from './MLRewriteCoveragesPage.messages';
import styles from './MLRewriteCoveragesPage.module.scss'
import { ScrollToError } from '@jutro/legacy/wizard-next';

function LobCoverages(props = {}){
    const layout = props.layout;
    if(layout === 'pa'){
        return <MLPAAgentEditCoveragesComponent {...props} />
    }
    if(layout === 'ho'){
        return <MLHOEditCoveragesComponent {...props} />
    }
    if(layout === 'umb'){
        return <MLPUPEditCoveragesComponent {...props} />
    }
    return <></>
}

const maxNumberVehicleShown = 3;
const emptyObj = {};
const EMPTY_STRING = 'Empty';

function MLRewriteCoveragesPage(props) {
    const {
        updateWizardData, wizardData, goNext, currentStep
    } = props;
    const {
        MLUWIssuesBlockingPoints
    } = appConfig;
    const { submissionVM } = wizardData;
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const [showErrors, updateShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const { isComponentValid, onValidate } = useValidation('rewriteCoverages');
    const checkRewriteSteps = useCheckRewriteSteps();
    const isGPA = appConfig.portalCode === 'GPA';

    const ModalService = MLModalService();

    // PA
    const [prevIndex, updatePrevIndex] = useState(0);
    const [nextIndex, updateNextIndex] = useState(Math.min(_.get(submissionVM, 'coverageLobs.personalAuto.vehicleCoverages.value.length', 0), maxNumberVehicleShown));
    const [isPrevAvailable, updateIsPrevAvailable] = useState(false);
    const [isNextAvailable, updateIsNextAvailable] = useState(false);
    const [lineCoverageNamesMap, updateLineCoverageNamesMap] = useState([]);
    const [vehicleCoverageNamesMap, updateVehicleCoverageNamesMap] = useState([]);
    const [optionalCoverageNamesMap, updateOptionalCoverageNamesMap] = useState([]);
    const [coverablesToDisplay, updateCoverablesToDisplay] = useState([]);
    const [isBlockedByUWIssues, updateIsBlockedByUWIssues] = useState(false);

    const onNext = useCallback(() => {
        if (!(submissionVM.aspects.valid && submissionVM.aspects.subtreeValid)) {
            updateShowErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }
        
        checkRewriteSteps.checkSteps(submissionVM);
        const hasVerbalAuthForms = checkRewriteSteps.hasVerbalAuthForms(submissionVM);
        const hasFormToBeSigned = checkRewriteSteps.hasFormToBeSigned(submissionVM);
        if(!hasVerbalAuthForms && !hasFormToBeSigned){
            submissionVM.value.portalCurrentStep_ML = currentStep.mlStepName
            const promise = MLRewriteService.bind(submissionVM.value, authHeader);
            ModalService.showProgressDialog(messages.bind, promise);
            promise.then((result) => {
                submissionVM.value = new Rewrite(result);
                updateWizardData(wizardData);

                if (isGPA) {
                    PolChangeTrackEvent('Rewrite Step Submitted', false, submissionVM, authHeader, {
                        step: 'PolChange Rewrite Coverage Screen',
                        action: 'Rewrite Coverage Submitted'
                    });
                }

                goNext();
            });
            promise.catch((error) => {
                error.modalTitle = translator(messages.bindErrorTitle);
                error.modalMessage =  translator(messages.bindErrorMessage);
                throw error;
            });
        }else{
            goNext();
        }
    }, [submissionVM, checkRewriteSteps, errorTimestamp, currentStep.mlStepName, authHeader, ModalService, updateWizardData, wizardData, isGPA, goNext, translator]);

    const getNextStepText = () => {
        return (
            <>
                {translator(messages.nextDescription, {
                    strong: (chunks) => <strong>{chunks}</strong>
                })}
            </>
        );
    };

    const getCoverablesToDisplay = useCallback((start, end) => {
        return submissionVM.coverageLobs.personalAuto.vehicleCoverages.value.slice(start, end);
    }, [submissionVM.coverageLobs]);

    const isPrevButtonAvailable = (_prevIndex) => {
        return _prevIndex !== 0;
    };

    const isNextButtonAvailable = useCallback((_nextIndex) => {
        return _nextIndex < submissionVM.coverageLobs.personalAuto.vehicleCoverages.value.length;
    }, [submissionVM.coverageLobs]);

    const initCoverableToDisplay = useCallback(() => {
        const _coverablesToDisplay = getCoverablesToDisplay(prevIndex, nextIndex);
        const _isPrevAvailable = isPrevButtonAvailable(prevIndex);
        const _isNextAvailable = isNextButtonAvailable(nextIndex);
        updateIsPrevAvailable(_isPrevAvailable);
        updateIsNextAvailable(_isNextAvailable);
        return _coverablesToDisplay;
    }, [getCoverablesToDisplay, isNextButtonAvailable, nextIndex, prevIndex]);

    const _findCoverageInMap = (coverageCode, map) => {
        return map.find((covInMap) => {
            return covInMap.coverageCode === coverageCode;
        });
    };

    const _findTermInMap = useCallback((coverageCode, termCode, map) => {
        const coverage = _findCoverageInMap(coverageCode, map);
        let isTermInMap = false;
        if (coverage) {
            isTermInMap = coverage.terms.find((termInMap) => {
                return termInMap.termCode === termCode;
            });
        }
        return isTermInMap;
    }, []);

    const extractTermNames = useCallback((coverage, map) => {
        coverage.terms.forEach((term) => {
            const covTermInMap = _findTermInMap(coverage.publicID, term.patternCode, map);
            if (!covTermInMap) {
                if (term.name !== EMPTY_STRING) {
                    const coverageMap = _findCoverageInMap(coverage.publicID, map);
                    coverageMap.terms.push({
                        termName: term.name,
                        termCode: term.patternCode,
                        isVisible_ML: term.isVisible_ML,
                        updated: !!term.updated
                    });
                }
            } else {
                covTermInMap.isVisible_ML = covTermInMap.isVisible_ML || term.isVisible_ML;
            }
        });
    }, [_findTermInMap]);

    const extractCoverageTermNames = useCallback((coverages, map) => {
        coverages.forEach((coverage) => {
            const covInMap = _findCoverageInMap(coverage.publicID, map);
            if (!covInMap) {
                if (coverage.selected && !(coverage.name === EMPTY_STRING || coverage.name.indexOf(`- ${EMPTY_STRING}`) > -1)) {
                        map.push({
                            coverageName: coverage.name,
                            coverageCode: coverage.publicID,
                            description: coverage.description,
                            required: coverage.required,
                            selected: coverage.selected,
                            terms: []
                        });
                }
            }
            if (coverage.terms && coverage.terms.length > 0) {
                extractTermNames(coverage, map);
            }
        });
    }, [extractTermNames]);

    const _sortCoverageTitleMaps = useCallback((_lineCoverageNamesMap, _optionalCoverageNamesMap, _vehicleCoverageNamesMap) => {
        _vehicleCoverageNamesMap = _.sortBy(_vehicleCoverageNamesMap, 'coverageName');
        _lineCoverageNamesMap = _.sortBy(_lineCoverageNamesMap, 'coverageName');
        _optionalCoverageNamesMap = _.sortBy(_optionalCoverageNamesMap, 'coverageName');
        return {
            _lineCoverageNamesMap, 
            _optionalCoverageNamesMap, 
            _vehicleCoverageNamesMap
        }
    }, []);

    const createCoverageTitles = useCallback((_coverablesToDisplay) => {
        const _lineCoverageNamesMap = [];
        const _optionalCoverageNamesMap = [];
        const _vehicleCoverageNamesMap = [];
        extractCoverageTermNames(submissionVM.coverageLobs.personalAuto.lineCoverages.value, _lineCoverageNamesMap);
        _coverablesToDisplay.forEach((vehicleCov) => {
            extractCoverageTermNames(vehicleCov.coverages, _vehicleCoverageNamesMap);
        });
        extractCoverageTermNames(submissionVM.coverageLobs.personalAuto.optionalCoverages_ML.value, _optionalCoverageNamesMap);
        const map = _sortCoverageTitleMaps(_lineCoverageNamesMap, _optionalCoverageNamesMap, _vehicleCoverageNamesMap);
        return map;
    }, [_sortCoverageTitleMaps, extractCoverageTermNames, submissionVM.coverageLobs]);

    const _sortCoverableCoverages = useCallback(() => {
        const sortedLineCoverages = _.sortBy(submissionVM.coverageLobs.personalAuto.lineCoverages.value, 'name');
        const sortedOptionalCoverages = _.sortBy(submissionVM.coverageLobs.personalAuto.optionalCoverages_ML.value, 'name');
        submissionVM.coverageLobs.personalAuto.lineCoverages.value = sortedLineCoverages;
        submissionVM.coverageLobs.personalAuto.optionalCoverages_ML.value = sortedOptionalCoverages;
        submissionVM.coverageLobs.personalAuto.vehicleCoverages.value.forEach((vehicle) => {
            const sortedVehicleCoverages = _.sortBy(vehicle.coverages, 'name');
            vehicle.coverages = sortedVehicleCoverages;
            let sortedCoverageTerms = [];
            vehicle.coverages.forEach((coverage) => {
                sortedCoverageTerms = _.sortBy(coverage.terms, 'patternCode');
            });
            vehicle.coverages.terms = sortedCoverageTerms;
        });
    }, [submissionVM.coverageLobs]);

    const _createEmptyTermObjs = (numberOfTerms) => {
        const emptyTerms = [];
        const termObj = {
            isVisible_ML: true,
            isEditable_ML: false
        }
        for (let i = 0; numberOfTerms > i; i++) {
            emptyTerms.push(termObj);
        }
        return emptyTerms;
    };

    const _createEmptyCoverageObject = useCallback((displayableCoverage) => {
        const retObj = {};
        retObj.name = `${displayableCoverage.coverageName} - ${EMPTY_STRING}`;
        retObj.publicID = displayableCoverage.coverageCode;
        retObj.coverageCode = displayableCoverage.coverageCode;
        retObj.isEmpty = true;
        retObj.selected = true;
        retObj.terms = [];
        const termsLength = displayableCoverage.terms.length;
        if (termsLength > 0) {
            retObj.terms = _createEmptyTermObjs(termsLength);
        }
        return retObj;
    }, []);

    const _addEmptyCoverageObject = useCallback((coverages, displayableCoverage, index) => {
        const dummyCoverageObj = _createEmptyCoverageObject(displayableCoverage);
        coverages.splice(index, 0, dummyCoverageObj);
    }, [_createEmptyCoverageObject]);

    const areCoverageTermsEqual = (availableTerms, termsToCheck) => {
        let isEqual = true;
        // check per term
        availableTerms.forEach((availableTerm) => {
            if (!termsToCheck.find((term) => term.patternCode === availableTerm.termCode)) {
                isEqual = false;
            }
        });
        // check overall
        if (isEqual) {
            isEqual = availableTerms.length === termsToCheck.length;
        }

        return isEqual;
    };

    const _createEmptyTermObjsForDifference = (displayableCoverage, coverage) => {
        displayableCoverage.terms.forEach((displayableTerm, index) => {
            if (!coverage.terms.find((term) => term.patternCode === displayableTerm.termCode)) {
                const dummyTermObj = {
                    name: EMPTY_STRING,
                    patternCode: displayableTerm.termCode,
                    isVisible_ML: true,
                    updated: false
                };
                coverage.terms.splice(index, 0, dummyTermObj);
                coverage.terms.join();
            }
        });
    };

    const processSyncTerms = useCallback((vehicleCoverage, displayableCoverage, syncedCoverages) => {
        if (vehicleCoverage) {
            if (!areCoverageTermsEqual(displayableCoverage.terms, vehicleCoverage.terms)) {
                _createEmptyTermObjsForDifference(displayableCoverage, vehicleCoverage);
            }
            syncedCoverages.push(vehicleCoverage);
        }
    }, []);

    const processSyncCoverages = useCallback((vehicle, map) => {
        map._vehicleCoverageNamesMap.forEach((displayableCoverage, index) => {
            const vehicleCoverage = vehicle.coverages.find((coverage) => coverage.publicID === displayableCoverage.coverageCode);
            if (!vehicleCoverage) {
                _addEmptyCoverageObject(vehicle.syncedCoverages, displayableCoverage, index);
            } else {
                processSyncTerms(vehicleCoverage, displayableCoverage, vehicle.syncedCoverages);
            }
        });
    }, [_addEmptyCoverageObject, processSyncTerms]);

    const filteredCoverages = (covs) =>{
        function removeCoverageIsTerms(data) {
        // Clone input to avoid mutations
        const cloned = structuredClone(data);

        // Helper to clean a coverages array (optional, vehicle, line, etc.)
        const cleanCoverages = (coverages) => {
            if (!Array.isArray(coverages)) return coverages;

            return coverages.map((cov) => ({
                ...cov,
                terms: Array.isArray(cov.terms)
                    ? cov.terms.filter((t) => t.name !== "Coverage Is")
                    : [],
            }));
        };

        // Remove from optionalCoverages_ML
        if (Array.isArray(cloned.optionalCoverages_ML)) {
            cloned.optionalCoverages_ML = cleanCoverages(cloned.optionalCoverages_ML);
        }

        // Remove from additionalCoverages_ML (usually no terms but included for consistency)
        if (Array.isArray(cloned.additionalCoverages_ML)) {
            cloned.additionalCoverages_ML = cleanCoverages(
                cloned.additionalCoverages_ML
            );
        }

        // Remove from lineCoverages
        if (Array.isArray(cloned.lineCoverages)) {
            cloned.lineCoverages = cleanCoverages(cloned.lineCoverages);
        }

        // Remove from vehicleCoverages (these contain nested coverages)
        if (Array.isArray(cloned.vehicleCoverages)) {
            cloned.vehicleCoverages = cloned.vehicleCoverages.map((vehicle) => ({
                ...vehicle,
                coverages: cleanCoverages(vehicle.coverages),
            }));
        }

        return cloned;
     }

     const coverages = removeCoverageIsTerms(covs);
     
     return coverages
    }

    const createCoverableCoverageMap = useCallback((_coverablesToDisplay, map) => {

        _sortCoverableCoverages();
        _coverablesToDisplay.forEach((vehicle) => {
            vehicle.syncedCoverages = [];
            processSyncCoverages(vehicle, map);
        });
    }, [_sortCoverableCoverages, processSyncCoverages]);

    const updateMaps = useCallback(() => {
        const covs = _.get(submissionVM, 'coverageLobs.personalAuto.value', []);

        if (!_.isEmpty(covs)) {
            const filteredCovs = filteredCoverages(covs);

            submissionVM.coverageLobs.personalAuto.value = filteredCovs
        }

        const _coverablesToDisplay = initCoverableToDisplay();
        const map = createCoverageTitles(_coverablesToDisplay);
        createCoverableCoverageMap(_coverablesToDisplay, map);

        updateCoverablesToDisplay(_coverablesToDisplay);
        updateLineCoverageNamesMap(map._lineCoverageNamesMap);
        updateOptionalCoverageNamesMap(map._optionalCoverageNamesMap);
        updateVehicleCoverageNamesMap(map._vehicleCoverageNamesMap);
    }, [createCoverableCoverageMap, createCoverageTitles, initCoverableToDisplay]);

    const onPrevButtonClick = () => {
        updatePrevIndex(prevIndex-1);
        updateNextIndex(nextIndex-1);
        updateIsPrevAvailable(isPrevButtonAvailable(prevIndex-1));
        updateIsNextAvailable(isNextButtonAvailable(nextIndex-1));
        const _coverablesToDisplay = getCoverablesToDisplay(prevIndex-1, nextIndex-1);
        const map = createCoverageTitles(_coverablesToDisplay);
        createCoverableCoverageMap(_coverablesToDisplay, map);

        updateCoverablesToDisplay(_coverablesToDisplay);
        updateLineCoverageNamesMap(map._lineCoverageNamesMap);
        updateOptionalCoverageNamesMap(map._optionalCoverageNamesMap);
        updateVehicleCoverageNamesMap(map._vehicleCoverageNamesMap);
    };

    const onNextButtonClick = () => {
        updatePrevIndex(prevIndex+1);
        updateNextIndex(nextIndex+1);
        updateIsPrevAvailable(isPrevButtonAvailable(prevIndex+1));
        updateIsNextAvailable(isNextButtonAvailable(nextIndex+1));
        const _coverablesToDisplay = getCoverablesToDisplay(prevIndex+1, nextIndex+1);
        const map = createCoverageTitles(_coverablesToDisplay);
        createCoverableCoverageMap(_coverablesToDisplay, map);

        updateCoverablesToDisplay(_coverablesToDisplay);
        updateLineCoverageNamesMap(map._lineCoverageNamesMap);
        updateOptionalCoverageNamesMap(map._optionalCoverageNamesMap);
        updateVehicleCoverageNamesMap(map._vehicleCoverageNamesMap);
    };

    const _checkisBlockedByUWIssues = () => {
        const result = MLUWService.isBlockedByUWIssues(submissionVM.underwritingIssues.value, MLUWIssuesBlockingPoints.BLOCKS_ISSUANCE);
        updateIsBlockedByUWIssues(result);
    };

    

    useEffect(() => {
        Identify('Rewrite Step Reached', {step: 'Coverages Screen'}, submissionVM, authHeader)
    }, [])

    useEffect(() => {
        if (_.get(submissionVM, 'coverageLobs.personalAuto.value')) {
            updateMaps();
        }
        _checkisBlockedByUWIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

     useEffect(() => {
         if (isGPA) {
             PolChangeTrackEvent('Rewrite Step Reached', false, submissionVM, authHeader, { step: 'PolChange Rewrite Coverage Screen' });
         }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPACoveragesProps = () => {
        return {
            coverages: submissionVM.coverageLobs.personalAuto.value,
            isDisplayOnly: true,
            prevIndex,
            nextIndex,
            isPrevAvailable,
            isNextAvailable,
            lineCoverageNamesMap,
            vehicleCoverageNamesMap,
            optionalCoverageNamesMap,
            coverablesToDisplay,
            updateMaps,
            onPrevButtonClick,
            onNextButtonClick,
            filterData: {},
            layout: 'pa',
            isRewrite: true
        }
    }

    const getHOCoveragesProps = () => {
        return {
            model: submissionVM.coverageLobs.homeowners.value,
            isDisplayOnly: true,
            showEditCoverage: false,
            isAgent: true,
            filterData: {selected: true},
            isQuoteStale: () => false,
            layout: 'ho'
        }
    }

    const getPUPCoveragesProps = () => {
        return {
            model: submissionVM.coverageLobs.personalUmbrella.value,
            isDisplayOnly: true,
            filterData: {selected: true},
            isQuoteStale: () => false,
            layout: 'umb'
        }
    }

    const getCoveragesProps = () => {
        if(submissionVM.value.isPersonalAuto()){
            return getPACoveragesProps();
        }
        if(submissionVM.value.isHomeOwners()){
            return getHOCoveragesProps();
        }
        if(submissionVM.value.isPersonalUmbrella()){
            return getPUPCoveragesProps();
        }
    }

    const getStickyHeaderLayout = () => {
        if(submissionVM.value.isPersonalAuto()){
            return 'pa';
        }
        if(submissionVM.value.isHomeOwners()){
            return 'ho';
        }
        if(submissionVM.value.isPersonalUmbrella()){
            return 'umb';
        }
    }

    const getPAStickyHeaderProps = () => {
        if(getStickyHeaderLayout() === "pa"){
            const model = {};
            for (const [coverageType, coveragesList] of Object.entries(submissionVM.coverageLobs.personalAuto.value)) {
                if (coverageType === 'vehicleCoverages') {
                    model[coverageType] = coveragesList;
                } else {
                    model[coverageType] = coveragesList.map(_.partial(Coverage.create, _, undefined, undefined));
                    model[coverageType] = _.sortBy(model[coverageType].filter((cov) => cov !== undefined), 'name');
                }
            }
            
            const isNamedNonOwner = () => {
                return _.get(model, 'vehicleCoverages', []).some((vehicle) => {
                    return vehicle.vehicleName === 'Named Non-Owner';
                });
            };

            return {
                model,
                isDisplayOnly: true,
                hasNamedNonOwner : isNamedNonOwner(),
                isPolicyChange: false,
                prevIndex,
                nextIndex,
                isPrevAvailable,
                isNextAvailable,
                coverablesToDisplay,
                updateMaps,
                onPrevButtonClick,
                onNextButtonClick,
                filterData: { selected: true }
            }
        }
        return {};
    }

    const overrideProps = {
        uwIssuesPanel: {
            uwIssues: submissionVM.underwritingIssues.value,
            jobID: submissionVM.jobNumber.value,
            rejectedIssuesAreQuoteBlocking: true,
            wrapped: true,
            approvedAllBlockingUWIssuesCallback: _checkisBlockedByUWIssues,
            submissionVM,
        },
        rewriteStickyHeader: {
            layout: getStickyHeaderLayout(),
            submissionVM,
            ...getPAStickyHeaderProps()
        },
        lobCoverages: getCoveragesProps(),
        nextButton: {
            disabled: isBlockedByUWIssues
        },
        nextDescription: {
            content: getNextStepText(),
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onNext
        },
        resolveComponentMap: {
            MLRewriteStickyHeader,
            LobCoverages,
            MLUWIssuesPanelComponent
        }
    }

    return (
        <WizardPage
            showCancel={false}
            showNext={false}
            showPrevious={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
                showErrors={showErrors}
                
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

export default MLRewriteCoveragesPage;